import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { AdminLayout } from '@/components/layout/admin-layout'
import { Loader2 } from 'lucide-react'

// Pages
import Login from '@/pages/Login'
import {
  Dashboard as AdminDashboard,
  Mitarbeiter,
  MitarbeiterDetail,
  Zeiterfassung,
  Krankmeldungen,
  Urlaub,
  Stundenzettel,
  Nachrichten,
  Bautagebuch,
  Bestellungen,
  BestellungDetail,
  Produkte,
  Bauvorhaben,
  Berichte,
  BauvorhabenStatistiken,
  Einstellungen,
  Benutzerverwaltung,
  KI,
  KIZaehler,
  KIPdfToMd,
  Maengel,
  MaengelDetail,
  Lieferanten,
  Geraete,
  Kalkulation,
  KalkulationDetail,
  ProjektControlling,
  DatanormSearch,
  Buchhaltung,
  Ausgangsrechnungen,
  Dokumentenablage,
  Fristen,
  BugTracker,
} from '@/pages/admin'
import { Dashboard as BauleiterDashboard, KIZaehler as BauleiterKIZaehler } from '@/pages/bauleiter'
import OrderForm from '@/pages/public/OrderForm'
import SignaturePage from '@/pages/public/SignaturePage'
import MitarbeiterUebersicht from '@/pages/public/MitarbeiterUebersicht'

function PageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
    </div>
  )
}

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRole?: string | null
  allowedRoles?: string[] | null
}

function ProtectedRoute({ children, requiredRole = null, allowedRoles = null }: ProtectedRouteProps) {
  const { user, loading, isAuthenticated } = useAuth()

  if (loading) return <PageSpinner />
  if (!isAuthenticated) return <Navigate to="/" replace />

  if (allowedRoles && Array.isArray(allowedRoles)) {
    if (!allowedRoles.includes(user?.role ?? '')) {
      if (user?.role === 'admin') return <Navigate to="/admin" replace />
      if (user?.role === 'bauleiter') return <Navigate to="/bauleiter" replace />
      return <Navigate to="/" replace />
    }
    return <>{children}</>
  }

  if (requiredRole && user?.role !== requiredRole) {
    if (user?.role === 'admin') return <Navigate to="/admin" replace />
    if (user?.role === 'bauleiter') return <Navigate to="/bauleiter" replace />
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

function LoginRoute() {
  const { user, loading, isAuthenticated } = useAuth()
  if (loading) return <PageSpinner />
  if (isAuthenticated) {
    if (user?.role === 'admin') return <Navigate to="/admin" replace />
    if (user?.role === 'bauleiter') return <Navigate to="/bauleiter" replace />
  }
  return <Login />
}

export default function App() {
  return (
    <Routes>
      <Route path="/" element={<LoginRoute />} />
      <Route path="/order/:token" element={<OrderForm />} />
      <Route path="/sign/:token" element={<SignaturePage />} />

      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="buchhaltung" element={<Buchhaltung />} />
        <Route path="ausgangsrechnungen" element={<Ausgangsrechnungen />} />
        <Route path="dokumentenablage" element={<Dokumentenablage />} />
        <Route path="fristen" element={<Fristen />} />
        <Route path="bug-tracker" element={<BugTracker />} />
        <Route path="mitarbeiter" element={<Mitarbeiter />} />
        <Route path="mitarbeiter/:id" element={<MitarbeiterDetail />} />
        <Route path="zeiterfassung" element={<Zeiterfassung />} />
        <Route path="krankmeldungen" element={<Krankmeldungen />} />
        <Route path="urlaub" element={<Urlaub />} />
        <Route path="stundenzettel" element={<Stundenzettel />} />
        <Route path="nachrichten" element={<Nachrichten />} />
        <Route path="bautagebuch" element={<Bautagebuch />} />
        <Route path="maengel" element={<Maengel />} />
        <Route path="maengel/:id" element={<MaengelDetail />} />
        <Route path="bestellungen" element={<Bestellungen />} />
        <Route path="bestellungen/:id" element={<BestellungDetail />} />
        <Route path="produkte" element={<Produkte />} />
        <Route path="lieferanten" element={<Lieferanten />} />
        <Route path="bauvorhaben" element={<Bauvorhaben />} />
        <Route path="berichte" element={<Berichte />} />
        <Route path="berichte/bauvorhaben" element={<BauvorhabenStatistiken />} />
        <Route path="einstellungen" element={<Einstellungen />} />
        <Route path="benutzerverwaltung" element={<Benutzerverwaltung />} />
        <Route path="ki" element={<KI />} />
        <Route path="ki/zaehler" element={<KIZaehler />} />
        <Route path="ki/pdf-to-md" element={<KIPdfToMd />} />
        <Route path="geraete" element={<Geraete />} />
        <Route path="kalkulation" element={<Kalkulation />} />
        <Route path="kalkulation/datanorm" element={<DatanormSearch />} />
        <Route path="kalkulation/:id" element={<KalkulationDetail />} />
        <Route path="controlling/:budgetId" element={<ProjektControlling />} />
        <Route path="uebersicht" element={<MitarbeiterUebersicht />} />
      </Route>

      <Route path="/bauleiter" element={<ProtectedRoute requiredRole="bauleiter"><BauleiterDashboard /></ProtectedRoute>} />
      <Route path="/bauleiter/ki/zaehler" element={<ProtectedRoute requiredRole="bauleiter"><BauleiterKIZaehler /></ProtectedRoute>} />
      <Route path="/bauleiter/*" element={<ProtectedRoute requiredRole="bauleiter"><BauleiterDashboard /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
